import { AfterViewInit, Component, ViewChild } from '@angular/core';
import {
  AbstractI18nService,
  AgbDialogComponent,
  AgbDialogComponentData,
  BreakpointService,
} from '@libs/index';
import { NavigationStart, Router } from '@angular/router';
import { BaseComponent } from '@libs/index';
import { MatSidenav, MatSidenavContent } from '@angular/material/sidenav';
import { takeUntil } from 'rxjs/operators';
import { NavigationService } from '../../../services/navigation/navigation.service';
import { StateService } from '../../../services/state/state.service';
import { User } from '@libs/index';
import { AuthenticationService } from '../../../services/authentication/authentication.service';
import { UserService } from '@libs/index';
import { DialogService } from '@libs/index';
import { LanguageSelectionDialogComponent } from '@libs/index';
import { FormControl } from '@angular/forms';
import { Agb } from '@libs/index';
import { UserUtils } from '@libs/index';
import { Language } from '@libs/index';
import {
  AgbToAcceptDialogComponent,
  AgbToAcceptDialogComponentData,
} from '../agb-to-accept-dialog/agb-to-accept-dialog.component';
import { ErrorHandlerServiceService } from '@libs/index';
import { DateTimeUtils } from '@libs/index';
import { DATE_FORMAT } from '@libs/index';
import {
  ContactUpdateDialogComponent,
  ContactUpdateDialogComponentData,
} from '../contact-update-dialog/contact-update-dialog.component';
import { ResendPinCountdownService } from '@libs/index';
import { IframeService } from '../../../services/iframe/iframe.service';

@Component({
  selector: 'app-frame',
  templateUrl: './app-frame.component.html',
  styleUrls: ['./app-frame.component.scss'],
})
export class AppFrameComponent extends BaseComponent implements AfterViewInit {
  public isSideNavOpened: boolean;

  public isHeaderTransparent: boolean;
  public showAgbHint: boolean;
  public selectedLanguage: Language;

  public agb: Agb;

  @ViewChild('sidenav') sidenav: MatSidenav;
  @ViewChild('sidenavContent') sidenavContent: MatSidenavContent;

  private controlUser = new FormControl(null);
  private isRequiredAgbDialogOpening = false;
  private isContactUpdateDialogOpening = false;
  private resendPinCountdownInSecond: number;

  constructor(
    public iframeService: IframeService,
    public breakpointService: BreakpointService,
    private router: Router,
    private errorHandlerServiceService: ErrorHandlerServiceService,
    private stateService: StateService,
    private dialogService: DialogService,
    private i18nService: AbstractI18nService,
    private navigationService: NavigationService,
    private resendPinCountdownService: ResendPinCountdownService,
    private authenticationService: AuthenticationService,
    private userService: UserService
  ) {
    super();

    this.router.events
      .pipe(takeUntil(this.componentDestroyed))
      .subscribe((event) => {
        if (event instanceof NavigationStart) {
          const targetPathname = event.url?.split('?')?.[0];
          // not scroll if only query parameter changes
          if (
            this.sidenavContent &&
            window.location.pathname !== targetPathname
          ) {
            this.sidenavContent.scrollTo({ top: 0 });
          }
          this.isHeaderTransparent =
            navigationService.isHomePage(targetPathname);
        }
      });
    this.isHeaderTransparent = navigationService.isHomePage(
      window.location.pathname
    );

    this.breakpointService.isTabletViewObservable
      .pipe(takeUntil(this.componentDestroyed))
      .subscribe((isMatched) => {
        if (!isMatched && this.sidenav) {
          this.sidenav.toggle(false);
        }
      });

    this.stateService.scrollToPosition$
      .pipe(takeUntil(this.componentDestroyed))
      .subscribe((position) => {
        if (this.sidenavContent) {
          this.sidenavContent.scrollTo({ top: position, behavior: 'smooth' });
        }
      });

    this.i18nService.selectedLanguage$
      .pipe(takeUntil(this.componentDestroyed))
      .subscribe((l) => (this.selectedLanguage = l));

    this.resendPinCountdownService.resendPinCountdownInSecond$
      .pipe(takeUntil(this.componentDestroyed))
      .subscribe((v) => {
        this.resendPinCountdownInSecond = v;
        this.checkAndRequireContactUpdate();
      });

    this.controlUser.valueChanges
      .pipe(takeUntil(this.componentDestroyed))
      .subscribe((user) => {
        if (!user) {
          this.agb = null;
        } else {
          this.fetchAndCheckValidityOfAgbAndContact();
        }
      });

    this.stateService.cookiesConfig$
      .pipe(takeUntil(this.componentDestroyed))
      .subscribe((cookiesConfig) => {
        if (cookiesConfig?.essential) {
          this.checkValidityOfAgbAndContact();
        }
      });

    this.controlUser.setValue(this.stateService.getUser());
    this.stateService.user$
      .pipe(takeUntil(this.componentDestroyed))
      .subscribe((user) => this.controlUser.setValue(user));
  }

  ngAfterViewInit() {
    this.sidenavContent
      .elementScrolled()
      .pipe(takeUntil(this.componentDestroyed))
      .subscribe((event) => {
        // @ts-ignore
        this.stateService.setScrollPosition(event?.target?.scrollTop);
      });

    if (this.stateService.getToken()?.length) {
      this.fetchCurrentLoggedInUser();
    }
  }

  public get user(): User {
    return this.controlUser.value;
  }

  public toggleSideNav(): void {
    this.sidenav.toggle();
  }

  public closeSideNav(): void {
    this.sidenav.close();
  }

  public async navigateToLogin() {
    await this.navigationService.navigateToLogin(window.location.href);
  }

  public async navigateToRegister() {
    await this.navigationService.navigateToRegister();
  }

  public async navigateToUserProfile() {
    await this.navigationService.navigateToUserProfile();
    this.closeSideNav();
  }

  public async navigateToSalonVisited() {
    await this.navigationService.navigateToSalonVisited();
    this.closeSideNav();
  }

  public async navigateToAppointmentHistory() {
    await this.navigationService.navigateToAppointmentHistory();
    this.closeSideNav();
  }

  public openLanguageDialog(): void {
    this.dialogService.openDialog(
      LanguageSelectionDialogComponent,
      this.dialogService.createSmallDialogConfig(null)
    );
  }

  public openAgbReadonlyDialog(): void {
    this.dialogService.openDialog(
      AgbDialogComponent,
      this.dialogService.createLargeDialogConfig({
        agb: this.agb,
      } as AgbDialogComponentData)
    );

    this.silentlyAcceptAgb();
  }

  public hideAgbHint(): void {
    this.showAgbHint = false;
    this.stateService.isClosedAgbHint = true;
  }

  public async navigateToPartnerLandingPage() {
    this.navigationService.navigateToPartnerLandingPage();
    this.closeSideNav();
  }

  public async navigateToTopSalons() {
    await this.navigationService.navigateToTopSalons();
    this.closeSideNav();
  }

  public async logout() {
    await this.authenticationService.logoutAndSaveRedirectLink(null);

    if (this.navigationService.isPrivateRoute(this.router.url)) {
      await this.navigationService.navigateToHome();
    }

    if (this.isSideNavOpened && !this.breakpointService.isPhoneView()) {
      this.toggleSideNav();
    }
  }

  private async fetchCurrentLoggedInUser() {
    try {
      const user = await this.userService.getCurrentLoggedInUser();
      this.stateService.setUser(user);
    } catch (e) {}
  }

  private async fetchAndCheckValidityOfAgbAndContact() {
    try {
      this.agb = await this.userService.getAgb(
        UserUtils.getAgbLocaleFromSelectedLanguage(this.selectedLanguage)
      );

      this.checkValidityOfAgbAndContact();
    } catch (e) {
      this.errorHandlerServiceService.handleError(e);
    }
  }

  private checkValidityOfAgbAndContact(): void {
    if (
      !this.stateService.getCookiesConfig()?.essential ||
      !this.agb ||
      this.isRequiredAgbDialogOpening
    ) {
      return;
    }
    const currentDate: string = DateTimeUtils.formatDateToString(
      new Date(),
      DATE_FORMAT.DATE_FORMAT_BACKEND
    );
    const isAgbVersionRequired: boolean = this.agb?.validSince <= currentDate;
    const isAgbSeen: boolean =
      this.user.agbAgreement?.lastSeenVersion >= this.agb?.version;

    if (
      !this.user.agbAgreement?.accepted ||
      (!isAgbSeen && isAgbVersionRequired)
    ) {
      const dialogRef = this.dialogService.openDialog(
        AgbToAcceptDialogComponent,
        this.dialogService.makeUnableToClose(
          this.dialogService.createLargeDialogConfig({
            agb: this.agb,
            user: this.user,
          } as AgbToAcceptDialogComponentData)
        )
      );
      this.isRequiredAgbDialogOpening = true;

      dialogRef
        .afterClosed()
        .pipe(takeUntil(this.componentDestroyed))
        .subscribe(() => {
          this.isRequiredAgbDialogOpening = false;
          this.checkAndRequireContactUpdate();
        });
    } else if (!isAgbSeen && !this.stateService.isClosedAgbHint) {
      this.showAgbHint = true;
      this.checkAndRequireContactUpdate();
    } else {
      this.checkAndRequireContactUpdate();
    }
  }

  private checkAndRequireContactUpdate(): void {
    if (
      !this.stateService.getCookiesConfig()?.essential ||
      this.isRequiredAgbDialogOpening ||
      this.isContactUpdateDialogOpening ||
      this.stateService.isClosedContactUpdateDialog ||
      this.resendPinCountdownInSecond > 0
    ) {
      return;
    }

    if (
      !!this.user?.id &&
      (!this.user.emailConfirmed || !this.user.phoneNrConfirmed)
    ) {
      const dialogRef = this.dialogService.openDialog(
        ContactUpdateDialogComponent,
        this.dialogService.createDefaultDialogConfig({
          user: this.user,
        } as ContactUpdateDialogComponentData)
      );

      this.isContactUpdateDialogOpening = true;

      dialogRef
        .afterClosed()
        .pipe(takeUntil(this.componentDestroyed))
        .subscribe(() => {
          this.stateService.isClosedContactUpdateDialog = true;
          this.isContactUpdateDialogOpening = false;
        });
    }
  }

  private async silentlyAcceptAgb() {
    try {
      await this.userService.updateAgbAgreement(
        this.user?.id,
        this.agb?.version
      );
    } catch (e) {
      this.errorHandlerServiceService.handleError(e);
    }
  }
}
